import { IProfile } from '@/@types/Onboarding';
import { useAuth } from '@/auth/Auth';
import CustomInputMask from '@/components/shared/CustomInputMask/CustomInputMask';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { Input } from '@/components/shared/Input/Input';
import { PhoneInput } from '@/components/shared/PhoneInput/PhoneInput';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { CountryDropdown, type Country } from '@/components/ui/country-dropdown';
import { Label } from '@/components/ui/label';
import { SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { dateMask, currencyOptions, formatCurrency } from '@/lib/select-options';
import { cnpjMask, cpfMask, userInitials } from '@/lib/utils';
import { format, isValid as isValidDateFns, parseISO } from 'date-fns';
import { CornerDownRight, Info, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeUrlOnBlur, normalizeUrlWhenPaste } from '@/utils/normalize';
import OnboardingSheet from '../../OnboardingSheet';
import { useOnboardingProfileStep, MAX_AVERAGE_MONTHLY_REVENUE_CENTS } from '../hooks/useOnBoardingProfileStep';
import { ProfileService } from '@/services/profile.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { getDefaultPhonePrefixByLanguage } from '@/utils/browser-locale-defaults';
import { sanitizePlainText } from '@/utils/sanitizePlainText';

function formatRevenueFromCents(cents: number, currency: string) {
  return formatCurrency(cents / 100, currency || 'USD');
}

interface ProfileStepProps {
  open: boolean;
  onClose: () => void;
  profile: IProfile | null;
  isPending: boolean;
  isEditing?: boolean;
  rejectionReason?: string | null;
  isProfileRejected?: boolean;
  canEdit?: boolean;
  isOnboarding?: boolean;
  isProfileStepComplete?: boolean;
}

export function ProfileStep({
  open,
  onClose,
  profile,
  isPending,
  isEditing,
  rejectionReason,
  isProfileRejected = false,
  canEdit = true,
  isOnboarding = false,
  isProfileStepComplete = false,
}: ProfileStepProps) {
  const { authData } = useAuth();
  const { t } = useTranslation(['onboarding', 'common']);
  const browserDefaultPhonePrefix = getDefaultPhonePrefixByLanguage();
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    profile?.user?.profile_photo?.url ?? null,
  );
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [formattedRevenue, setFormattedRevenue] = useState('');
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    errors,
    setValue,
    onSubmit,
    isCreatingProfile,
    defaultValues,
    setError,
    clearErrors,
    register,
    trigger,
  } = useOnboardingProfileStep({
    onClose,
    profile,
    mode: isEditing ? 'edit' : 'create',
    isProfileRejected,
    isOnboarding,
    isProfileStepComplete,
  });

  const skipAboutYouQuestions = !isOnboarding || isProfileStepComplete;

  const [showAdditionalSections, setShowAdditionalSections] = useState(
    skipAboutYouQuestions,
  );

  const revenueCurrency = defaultValues?.average_monthly_revenue_currency || 'USD';

  useEffect(() => {
    if (!open) return;
    setShowAdditionalSections(skipAboutYouQuestions);
  }, [open, skipAboutYouQuestions]);

  const handleContinueAboutYou = async () => {
    const isValid = await trigger([
      'heard_about_mundpay',
      'average_monthly_revenue',
      'average_monthly_revenue_currency',
      'product_type_sold',
    ]);
    if (!isValid) return;
    setShowAdditionalSections(true);
  };

  const aboutYouFields = [
    'heard_about_mundpay',
    'average_monthly_revenue',
    'average_monthly_revenue_currency',
    'product_type_sold',
  ] as const;

  const onInvalidSubmit = (formErrors: typeof errors) => {
    const hasAboutYouError = aboutYouFields.some((field) => formErrors[field]);
    if (hasAboutYouError) {
      setShowAdditionalSections(false);
    }
    toast({
      description: t('common:required_field'),
      variant: 'error',
    });
  };

  useEffect(() => {
    if (!isEditing) {
      setValue('full_name', authData?.name ?? '');
    }
  }, [authData?.name, isEditing, setValue]);

  useEffect(() => {
    setAvatarPreview(profile?.user?.profile_photo?.url ?? null);
  }, [profile?.user?.profile_photo?.url]);

  useEffect(() => {
    const cents = defaultValues?.average_monthly_revenue;
    if (typeof cents === 'number' && cents > 0) {
      setFormattedRevenue(formatRevenueFromCents(cents, revenueCurrency));
    } else if (!cents) {
      setFormattedRevenue('');
    }
  }, [defaultValues?.average_monthly_revenue, revenueCurrency]);

  const handleAboutYouTextChange =
    (field: 'heard_about_mundpay' | 'product_type_sold') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizePlainText(e.target.value);
      e.target.value = sanitized;
      setValue(field, sanitized, { shouldValidate: true });
      clearErrors(field);
    };

  const handleRevenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const valueInCents = Math.min(
      parseInt(rawValue || '0', 10),
      MAX_AVERAGE_MONTHLY_REVENUE_CENTS,
    );
    setFormattedRevenue(
      valueInCents > 0
        ? formatRevenueFromCents(valueInCents, revenueCurrency)
        : '',
    );
    setValue('average_monthly_revenue', valueInCents > 0 ? valueInCents : null, {
      shouldValidate: true,
    });
    clearErrors('average_monthly_revenue');
  };

  const handleRevenueCurrencyChange = (currency: string) => {
    setValue('average_monthly_revenue_currency', currency, {
      shouldValidate: true,
    });
    clearErrors('average_monthly_revenue_currency');
    const cents = defaultValues?.average_monthly_revenue;
    if (typeof cents === 'number' && cents > 0) {
      setFormattedRevenue(formatRevenueFromCents(cents, currency));
    }
  };

  const isBrazilianNationality =
    (defaultValues?.nationality || 'BR').toUpperCase() === 'BR';

  const { mutateAsync: uploadPhoto } = useMutation({
    mutationFn: async (file: File) => await ProfileService.uploadProfilePhoto(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });
      toast({ description: t('common:data_saved'), variant: 'success' });
    },
    onError: (error) => {
      toast({
        description: ProfileService.getProfilePhotoUploadErrorMessage(error, t),
        variant: 'error',
      });
    },
  });

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > ProfileService.PROFILE_PHOTO_MAX_BYTES) {
      toast({
        description: t('common:file_too_large', {
          size: ProfileService.PROFILE_PHOTO_MAX_MB,
        }),
        variant: 'error',
      });
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploadingPhoto(true);
    uploadPhoto(file).finally(() => {
      setIsUploadingPhoto(false);
      // Permite selecionar o mesmo arquivo novamente
      event.target.value = '';
    });
  };

  function parseMaskedDateToIso(rawInput: string) {
    if (rawInput.length > 10) return { kind: 'ignore' as const };

    const raw = rawInput.replace(/\D/g, '');
    const limitedRaw = raw.slice(0, 8);

    if (limitedRaw.length !== 8) return { kind: 'incomplete' as const };

    const day = limitedRaw.slice(0, 2);
    const month = limitedRaw.slice(2, 4);
    const year = limitedRaw.slice(4, 8);

    const date = new Date(Number(year), Number(month) - 1, Number(day));
    const isValidDate =
      date instanceof Date &&
      !isNaN(date.getTime()) &&
      date.getDate() === Number(day) &&
      date.getMonth() === Number(month) - 1 &&
      date.getFullYear() === Number(year);

    if (!isValidDate) return { kind: 'invalid' as const };

    const iso = `${year}-${month}-${day}`;
    return { kind: 'valid' as const, iso, date, year: Number(year) };
  }

  function toStartOfDay(date: Date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function handleBirthDateInput(rawInput: string) {
    const parsed = parseMaskedDateToIso(rawInput);
    if (parsed.kind === 'ignore') return;

    if (parsed.kind !== 'valid') {
      setValue('birth_date', '');
      clearErrors('birth_date');
      return;
    }

    const today = toStartOfDay(new Date());
    const birthDate = toStartOfDay(parsed.date);
    const minDate = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate(),
    );

    if (parsed.year < 1900 || birthDate > today) {
      setError('birth_date', {
        type: 'manual',
        message: t('common:invalid_date'),
      });
      setValue('birth_date', '');
      return;
    }

    if (birthDate > toStartOfDay(minDate)) {
      setError('birth_date', {
        type: 'manual',
        message: t('common:must_be_18_or_older'),
      });
      setValue('birth_date', '');
      return;
    }

    clearErrors('birth_date');
    setValue('birth_date', parsed.iso);
  }

  function handleDocumentExpiryDateInput(rawInput: string) {
    const parsed = parseMaskedDateToIso(rawInput);
    if (parsed.kind === 'ignore') return;

    if (parsed.kind !== 'valid') {
      setValue('document_expiry_date', '');
      clearErrors('document_expiry_date');
      return;
    }

    const today = toStartOfDay(new Date());
    const expiryDate = toStartOfDay(parsed.date);

    if (parsed.year < 1900 || expiryDate < today) {
      setError('document_expiry_date', {
        type: 'manual',
        message: t('common:invalid_date'),
      });
      setValue('document_expiry_date', '');
      return;
    }

    clearErrors('document_expiry_date');
    setValue('document_expiry_date', parsed.iso);
  }

  function handleIncorporationDateInput(rawInput: string) {
    const parsed = parseMaskedDateToIso(rawInput);
    if (parsed.kind === 'ignore') return;

    if (parsed.kind !== 'valid') {
      setValue('incorporation_date', '');
      clearErrors('incorporation_date');
      return;
    }

    const today = toStartOfDay(new Date());
    const incorporationDate = toStartOfDay(parsed.date);

    if (parsed.year < 1900 || incorporationDate > today) {
      setError('incorporation_date', {
        type: 'manual',
        message: t('common:invalid_date'),
      });
      setValue('incorporation_date', '');
      return;
    }

    clearErrors('incorporation_date');
    setValue('incorporation_date', parsed.iso);
  }

  const documentsOptions = React.useMemo(
    () =>
      isBrazilianNationality
        ? [
            { value: 'individual', label: t('common:documents.individual') },
            { value: 'business', label: t('common:documents.business') },
          ]
        : [
            { value: 'individual', label: 'Individual' },
            { value: 'business', label: 'Business' },
          ],
    [t, isBrazilianNationality]
  );

  const documentTypeOptions = React.useMemo(
    () => [
      { value: 'passport', label: t('document_type.passport') },
      { value: 'national_id', label: t('document_type.national_id') },
      { value: 'drivers_license', label: t('document_type.drivers_license') },
    ],
    [t]
  );

  // Se o perfil foi rejeitado, os campos de identidade devem estar desabilitados
  // (no onboarding, "edit" significa apenas usar update, não travar os campos)
  const isOnboardingFinished = authData?.onboarding_step === 'finished';
  // Se o onboarding está "finished" mas o step de perfil não está concluído,
  // ainda precisamos permitir edição (ex.: onboarding_detail.profile === false).
  // Aqui usamos `isEditing` como proxy de "perfil já existe/concluído".
  const isProfileReadOnly = isOnboardingFinished && Boolean(isEditing);

  const hasRejectionReason = Boolean(rejectionReason);

  const isIdentityFieldsDisabled =
    !canEdit || (!hasRejectionReason && (isProfileRejected || isProfileReadOnly));

  const isNameAndPhoneLocked =
    !canEdit ||
    authData?.documentation_status === 'approved' ||
    isProfileReadOnly ||
    isProfileRejected ||
    hasRejectionReason;

  return (
    <OnboardingSheet
      isPending={isPending}
      onClose={onClose}
      open={open}
      hasHeader={!isEditing}
    >
      <form
        onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
        className='flex flex-col justify-between h-[calc(100vh-140px)]'
      >
        <section className='p-6'>
          <SheetTitle>{t('profile')}</SheetTitle>
          {rejectionReason && (
            <Alert variant='default' className='mt-4 bg-yellow-500/10 border-yellow-500/50'>
              <Info size={16} className='text-yellow-500' />
              <AlertTitle className='text-yellow-500'>
                {t('review_required_title')}
              </AlertTitle>
              <AlertDescription className='text-yellow-100'>
                <p className='mb-2'>{t('review_required_description')}</p>
                <p className='font-medium'>{rejectionReason}</p>
              </AlertDescription>
            </Alert>
          )}
          <div className='flex items-center mt-6 gap-4'>
            <Avatar className='h-14 w-14 bg-blackA1'>
              <AvatarImage
                src={avatarPreview || ''}
                alt='Foto de perfil'
                className='h-full w-full'
              />
              <AvatarFallback className='bg-elevated text-xl font-medium text-muted'>
                {userInitials(authData?.name ?? '')}
              </AvatarFallback>
            </Avatar>
            <input
              type='file'
              id='avatar-input'
              accept='image/*'
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
            <Button
              className='bg-elevated text-colorText h-9'
              type='button'
              onClick={() => document.getElementById('avatar-input')?.click()}
              disabled={isUploadingPhoto || !canEdit}
            >
              {isUploadingPhoto ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin" />
                  {t('saving')}
                </span>
              ) : (
                t('change_photo')
              )}
            </Button>
          </div>

          <section className='mt-6 space-y-4 border-b border-b-colorBorder pb-8'>
            <Input
              label={t('email')}
              placeholder={t('email_placeholder')}
              type='email'
              defaultValue={authData?.email}
              disabled
            />
            <Input
              label={t('name')}
              placeholder={t('name')}
              helperText={errors.full_name?.message}
              {...register('full_name')}
              value={defaultValues?.full_name || ''}
              disabled={isNameAndPhoneLocked}
            />
            <PhoneInput
              label={t('phone_number')}
              phone_prefix={
                defaultValues?.phone_prefix ||
                authData?.phone_prefix ||
                browserDefaultPhonePrefix
              }
              value={defaultValues?.phone || ''}
              onChangePhone={(value, prefix) => {
                if (isNameAndPhoneLocked) return;
                setValue('phone', value);
                setValue('phone_prefix', prefix);
              }}
              disabled={isNameAndPhoneLocked}
            />
          </section>

          {isOnboarding && (
            <section className='mt-8 space-y-4 pb-8'>
              <SheetTitle>{t('tell_us_more_about_you')}</SheetTitle>

              <Input
                label={t('heard_about_mundpay_label')}
                placeholder={t('heard_about_mundpay_placeholder')}
                helperText={errors.heard_about_mundpay?.message}
                {...register('heard_about_mundpay')}
                value={defaultValues?.heard_about_mundpay || ''}
                onChange={handleAboutYouTextChange('heard_about_mundpay')}
                disabled={!canEdit}
              />

              <div className='flex gap-3 items-end w-full'>
                <div className='flex-1 min-w-0'>
                  <Input
                    label={t('average_monthly_revenue_label')}
                    placeholder={t('average_monthly_revenue_placeholder')}
                    inputMode='numeric'
                    value={formattedRevenue}
                    onChange={handleRevenueChange}
                    helperText={errors.average_monthly_revenue?.message}
                    disabled={!canEdit}
                  />
                </div>
                <div className='w-[110px] shrink-0'>
                  <CustomSelect
                    options={currencyOptions}
                    placeholder='USD'
                    onValueChange={handleRevenueCurrencyChange}
                    value={revenueCurrency}
                    disabled={!canEdit}
                  />
                </div>
              </div>

              <Input
                label={t('product_type_sold_label')}
                placeholder={t('product_type_sold_placeholder')}
                subLabel={t('product_type_sold_hint')}
                helperText={errors.product_type_sold?.message}
                {...register('product_type_sold')}
                value={defaultValues?.product_type_sold || ''}
                onChange={handleAboutYouTextChange('product_type_sold')}
                disabled={!canEdit}
              />

              {!showAdditionalSections && (
                <div className='flex justify-end pt-2'>
                  <Button
                    type='button'
                    variant='default'
                    onClick={handleContinueAboutYou}
                    disabled={!canEdit}
                  >
                    {t('continue')}
                  </Button>
                </div>
              )}
            </section>
          )}

          {showAdditionalSections && (
          <>
          <section className='mt-8 border-b border-b-colorBorder pb-8 space-y-4'>
            <SheetTitle>{t('country_and_account_type')}</SheetTitle>

            <div className='flex flex-col gap-2'>
              <Label>{t('nationality_label')}</Label>
              <CountryDropdown
                defaultValue={defaultValues?.nationality || 'BR'}
                onChange={(country) => {
                  const alpha2 = country.alpha2.toUpperCase();
                  setValue('nationality', alpha2);
                  setValue('residence_country', alpha2);

                  // Se for empresa e ainda não tiver país da empresa, usa o da nacionalidade
                  if (
                    defaultValues?.profile_type === 'business' &&
                    !defaultValues?.company_country
                  ) {
                    setValue('company_country', alpha2);

                    // Se ainda não tiver país de constituição, mantém alinhado
                    if (!defaultValues?.incorporation_country) {
                      setValue('incorporation_country', alpha2);
                    }
                  }

                  // Se ainda não tiver país emissor do documento, usa o da nacionalidade
                  if (!defaultValues?.document_issuer_country) {
                    setValue('document_issuer_country', alpha2);
                  }
                }}
                placeholder={t('select_nationality_placeholder')}
                disabled={isIdentityFieldsDisabled}
              />
            </div>
  

            <CustomSelect
              options={documentsOptions}
              label={t('profile_type')}
              placeholder={t('select_profile_type')}
              onValueChange={(e) => {
                setValue('profile_type', e)
                clearErrors('profile_type')
              }}
              helperText={errors.profile_type?.message}
              hasError={!!errors.profile_type}
              value={defaultValues?.profile_type}
              disabled={isIdentityFieldsDisabled}
            />

            {defaultValues?.profile_type === 'business' && (
              <div className='flex flex-col gap-2'>
                <Label>{t('company_country_label')}</Label>
                <CountryDropdown
                  defaultValue={defaultValues?.company_country || 'BR'}
                  onChange={(country) => {
                    const alpha2 = country.alpha2.toUpperCase();
                    setValue('company_country', alpha2);

                    // Se o país de constituição ainda não foi escolhido, acompanha o país da empresa
                    if (!defaultValues?.incorporation_country) {
                      setValue('incorporation_country', alpha2);
                    }
                  }}
                  placeholder={t('select_company_country_placeholder')}
                  disabled={isIdentityFieldsDisabled}
                />
              </div>
            )}
          </section>    

          {(defaultValues?.profile_type === 'individual' ||
            defaultValues?.profile_type === 'business') && (
                <div>
          <section className='mt-8'>
            <SheetTitle>{t('identity')}</SheetTitle>
          </section>
              <div className='flex items-center gap-2 mt-6'>
                <CornerDownRight className='text-muted' />
                <h2 className='text-muted font-semibold'>
                  {t('responsible_data')}
                </h2>
              </div>
              <Alert variant='default' className='bg-[#18181b] my-4'>
                <Info size={16} />
                <AlertDescription>
                  {t('responsible_data_description')}
                </AlertDescription>
              </Alert>
              <div className='grid grid-cols-2 mt-4 mb-6 gap-4'>
                {isBrazilianNationality ? (
                  <CustomInputMask
                    value={cpfMask(defaultValues?.personal_document)}
                    mask={cpfMask}
                    onChange={(e) => {
                      const onlyNumbers = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 11);
                      setValue('personal_document', onlyNumbers);
                      clearErrors('personal_document');
                    }}
                    label={'CPF'}
                    placeholder={'000.000.000-00'}
                    helperText={errors.personal_document?.message}
                    disabled={isIdentityFieldsDisabled}
                  />
                ) : (
                  <>
                    <CustomSelect
                      options={documentTypeOptions}
                      label={t('document_type_label')}
                      placeholder={t('select_document_type_placeholder')}
                        onValueChange={(e) => {
                          setValue('document_type', e);
                          clearErrors('document_type');
                      }}
                      helperText={errors.document_type?.message}
                        hasError={!!errors.document_type}
                        
                      value={defaultValues?.document_type}
                      disabled={isIdentityFieldsDisabled}
                    />

                    <Input
                      label={t('document_number_label')}
                      placeholder={t('document_number_placeholder')}
                      helperText={errors.personal_document?.message}
                      {...register('personal_document', {
                        setValueAs: (v) => v?.trim().replace(/\s/g, ''),
                      })}
                      disabled={isIdentityFieldsDisabled}
                    />

                    <div className='flex flex-col gap-2 col-span-2'>
                      <Label>{t('document_issuer_country_label')}</Label>
                      <CountryDropdown
                        defaultValue={defaultValues?.document_issuer_country || 'BR'}
                        onChange={(country) => {
                          const alpha2 = country.alpha2.toUpperCase();
                          setValue('document_issuer_country', alpha2);
                        }}
                        placeholder={t('select_document_issuer_country_placeholder')}
                        disabled={isIdentityFieldsDisabled}
                      />
                      {errors.document_issuer_country?.message && (
                        <p className='text-sm text-error'>
                          {String(errors.document_issuer_country.message)}
                        </p>
                      )}
                    </div>

                    <CustomInputMask
                      label={t('document_expiry_date_label')}
                      placeholder='dd/mm/aaaa'
                      inputMode='numeric'
                      mask={dateMask}
                      value={
                        defaultValues?.document_expiry_date &&
                        isValidDateFns(parseISO(defaultValues.document_expiry_date))
                          ? dateMask(
                              format(
                                parseISO(defaultValues.document_expiry_date),
                                'dd/MM/yyyy',
                              ),
                            )
                          : ''
                      }
                      onChange={(e) =>
                        handleDocumentExpiryDateInput(e.target.value)
                      }
                      helperText={errors.document_expiry_date?.message}
                      disabled={isIdentityFieldsDisabled}
                    />
                  </>
                )}
                <CustomInputMask
                  label={t('birth_date')}
                  placeholder='dd/mm/aaaa'
                  inputMode='numeric'
                  mask={dateMask}
                  value={
                    defaultValues?.birth_date &&
                    isValidDateFns(parseISO(defaultValues.birth_date))
                      ? dateMask(
                          format(
                            parseISO(defaultValues.birth_date),
                            'dd/MM/yyyy'
                          )
                        )
                      : ''
                  }
                  onChange={(e) => handleBirthDateInput(e.target.value)}
                  helperText={errors.birth_date?.message}
                  disabled={isIdentityFieldsDisabled}
                />
              </div>
            </div>
          )}

          {defaultValues?.profile_type === 'business' && (
            <div>
              <div className='flex items-center gap-2 mt-6 mb-4'>
                <CornerDownRight className='text-muted' />
                <h2 className='text-muted font-semibold'>
                  {t('company_data')}
                </h2>
              </div>
              {(defaultValues?.company_country || 'BR')?.toUpperCase() === 'BR' ? (
                <div className='space-y-4 mb-6'>
                  <Input
                    label={t('social_name')}
                    placeholder={t('social_name_placeholder')}
                    onChange={(e) => setValue('social_name', e.target.value)}
                    defaultValue={defaultValues?.social_name}
                    helperText={errors.social_name?.message}
                    disabled={isIdentityFieldsDisabled}
                  />
                  <CustomInputMask
                    mask={cnpjMask}
                    onChange={(e) => {
                      const onlyNumbers = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 14)
                      setValue('business_document', onlyNumbers)
                      clearErrors('business_document')
                    }}
                    value={cnpjMask(defaultValues?.business_document)}
                    label={'CNPJ'}
                    placeholder={t('cnpj_placeholder')}
                    helperText={errors.business_document?.message}
                    disabled={isIdentityFieldsDisabled}
                  />
                </div>
              ) : (
                <div className='space-y-4 mb-6'>
                  <Input
                    label={t('company_legal_name_label')}
                    placeholder={t('company_legal_name_placeholder')}
                    helperText={errors.social_name?.message}
                    {...register('social_name', {
                      setValueAs: (v) => v?.trim(),
                    })}
                    disabled={isIdentityFieldsDisabled}
                  />

                  <Input
                    label={t('company_trade_name_label')}
                    placeholder={t('company_trade_name_placeholder')}
                    helperText={errors.company_trade_name?.message}
                    {...register('company_trade_name', {
                      setValueAs: (v) => v?.trim(),
                    })}
                    disabled={isIdentityFieldsDisabled}
                  />

                  <Input
                    label={t('company_registration_number_label')}
                    placeholder={t('company_registration_number_placeholder')}
                    helperText={errors.business_document?.message}
                    {...register('business_document', {
                      setValueAs: (v) => v?.trim(),
                    })}
                    disabled={isIdentityFieldsDisabled}
                  />

                  <div className='grid w-full items-center gap-2'>
                    <Label className='!text-white'>
                      {t('company_address_label')}
                    </Label>
                    <Textarea
                      placeholder={t('company_address_placeholder')}
                      className='min-h-[96px]'
                      {...register('company_address', {
                        setValueAs: (v) => v?.trim(),
                      })}
                      disabled={isIdentityFieldsDisabled}
                    />
                    {errors.company_address?.message && (
                      <p className='text-sm text-error'>
                        {String(errors.company_address.message)}
                      </p>
                    )}
                  </div>

                  <div className='flex flex-col gap-2'>
                    <Label>{t('incorporation_country_label')}</Label>
                    <CountryDropdown
                      defaultValue={defaultValues?.incorporation_country || 'BR'}
                      onChange={(country) => {
                        const alpha2 = country.alpha2.toUpperCase();
                        setValue('incorporation_country', alpha2);
                      }}
                      placeholder={t('select_incorporation_country_placeholder')}
                      disabled={isIdentityFieldsDisabled}
                    />
                    {errors.incorporation_country?.message && (
                      <p className='text-sm text-error'>
                        {String(errors.incorporation_country.message)}
                      </p>
                    )}
                  </div>

                  <CustomInputMask
                    label={'Data de constituição'}
                    placeholder='dd/mm/aaaa'
                    inputMode='numeric'
                    mask={dateMask}
                    value={
                      defaultValues?.incorporation_date &&
                      isValidDateFns(parseISO(defaultValues.incorporation_date))
                        ? dateMask(
                            format(
                              parseISO(defaultValues.incorporation_date),
                              'dd/MM/yyyy',
                            ),
                          )
                        : ''
                    }
                    onChange={(e) => handleIncorporationDateInput(e.target.value)}
                    helperText={errors.incorporation_date?.message}
                    disabled={isIdentityFieldsDisabled}
                  />

                  <Input
                    label={t('company_type_label')}
                    placeholder={t('company_type_placeholder')}
                    helperText={errors.company_type?.message}
                    {...register('company_type', {
                      setValueAs: (v) => v?.trim(),
                    })}
                    disabled={isIdentityFieldsDisabled}
                  />

                  <Input
                    label={t('company_industry_label')}
                    placeholder={t('company_industry_placeholder')}
                    helperText={errors.company_industry?.message}
                    {...register('company_industry', {
                      setValueAs: (v) => v?.trim(),
                    })}
                    disabled={isIdentityFieldsDisabled}
                  />

                  <Input
                    label={t('company_website_label')}
                    placeholder={t('company_website_placeholder')}
                    helperText={errors.company_website?.message}
                    {...register('company_website', {
                      setValueAs: (v) => v?.trim().replace(/\s/g, ''),
                    })}
                    onFocus={(e) => {
                      if (!e.currentTarget.value) {
                        e.currentTarget.value = 'https://'
                        setValue('company_website', 'https://')
                      }
                    }}
                    onPaste={(e) => {
                      const finalValue = normalizeUrlWhenPaste(e)
                      setValue('company_website', finalValue)
                    }}
                    onBlur={(e) => {
                      const normalized = normalizeUrlOnBlur(e.currentTarget.value)
                      e.currentTarget.value = normalized
                      setValue('company_website', normalized)
                    }}
                    disabled={isIdentityFieldsDisabled}
                  />
                </div>
              )}
            </div>
          )}
          </>
          )}
        </section>

        <SheetFooter className='flex gap-2 border-t border-t-colorBorder py-4 px-6 max-md:pb-24'>
          <div className='flex justify-end items-center w-full gap-2'>
            <Button variant='ghost' type='button' onClick={onClose}>
              {t('cancel')}
            </Button>
            {showAdditionalSections && (
            <Button
              variant='default'
              type='submit'
              disabled={!canEdit || (!hasRejectionReason && isProfileReadOnly) || isCreatingProfile}
            >
              {isCreatingProfile ? (
                <span className='flex items-center gap-2'>
                  <Loader2 className='animate-spin' />
                  {t('saving')}
                </span>
              ) : (
                t('save')
              )}
            </Button>
            )}
          </div>
        </SheetFooter>
      </form>
    </OnboardingSheet>
  );
}